// TrendChip — a compact ↑/↓/→ chip that compares a new lab value with the
// most recent previous value for the same canonical test.
//
//   <TrendChip testName="HbA1c" value="7.2" latestMap={latest} />
//
// latestMap comes from GET /api/patients/:pid/latest-investigations and has
// the shape { hba1c: { value, unit, date, test_name }, ldl: {...}, ... }.
//
// Rules of "better / worse":
//   • Lower-is-better tests (LDL, TC, HbA1c, TG, non-HDL, creatinine, ALT/AST,
//     FBS, PPBS, RBS, urea, uric acid, ACR, urine albumin, weight, BMI) —
//     a drop is GREEN, a rise is RED.
//   • Higher-is-better tests (eGFR, HDL, haemoglobin, vitamin D, vitamin B12,
//     platelets, albumin, total protein) — a rise is GREEN, a drop is RED.
//   • Everything else (TSH, T3, T4, sodium, calcium, insulin) — grey /
//     "changed" only; no colour bias.
//
// The chip is intentionally tiny so it can sit next to a table cell.

import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

// Canonical key -> "better direction" ("down" | "up" | null).
const DIRECTION = {
  hba1c: "down", ldl: "down", tc: "down", triglycerides: "down", non_hdl: "down",
  creatinine: "down", urea: "down", uric_acid: "down",
  alt: "down", ast: "down", ggt: "down",
  fbs: "down", ppbs: "down", rbs: "down",
  urine_alb: "down", urine_creat: null, acr: "down",
  hdl: "up", hb: "up", vit_d: "up", vit_b12: "up", platelets: "up",
  albumin: "up", total_protein: "up", egfr: "up",
  weight: "down", bmi: "down",
  tsh: null, t3: null, t4: null, insulin: null,
  sodium: null, calcium: null, potassium: null,
  chloride: null, magnesium: null, phosphorus: null, bicarbonate: null,
};

// Same needle map as backend _TREND_KEYS (kept in sync — small enough not to
// warrant an API round-trip for every row).
const KEY_MATCH = [
  ["hba1c", ["hba1c", "hb a1c", "glycated haemoglobin", "glycated hemoglobin", "a1c"]],
  ["ldl", ["ldl-c", "ldl cholesterol", "ldl"]],
  ["hdl", ["hdl-c", "hdl cholesterol", "hdl"]],
  ["tc", ["total cholesterol", "cholesterol total"]],
  ["triglycerides", ["triglyceride", "trig", "tg "]],
  ["non_hdl", ["non-hdl", "non hdl", "nonhdl"]],
  ["creatinine", ["creatinine", "s.creat", "s creat"]],
  ["egfr", ["egfr", "e gfr", "gfr"]],
  ["urea", ["blood urea", "urea"]],
  ["fbs", ["fasting blood sugar", "fasting glucose", "fasting plasma glucose", "fbs"]],
  ["ppbs", ["post prandial", "postprandial", "post-prandial", "ppbs", "pp sugar"]],
  ["rbs", ["random blood sugar", "random sugar", "rbs"]],
  ["insulin", ["fasting insulin", "insulin"]],
  ["tsh", ["tsh"]],
  ["t3", ["free t3", "ft3", "t3"]],
  ["t4", ["free t4", "ft4", "t4"]],
  ["hb", ["haemoglobin", "hemoglobin", "hb "]],
  ["potassium", ["potassium", "s.potassium", "serum potassium", "k+", "s.k", "s k"]],
  ["chloride", ["chloride", "s.chloride", "serum chloride"]],
  ["bicarbonate", ["bicarbonate", "hco3", "s.hco3"]],
  ["magnesium", ["magnesium", "s.magnesium", "mg2+"]],
  ["phosphorus", ["phosphorus", "phosphate", "s.phosphate"]],
  ["bun", ["blood urea nitrogen", "bun"]],
  ["bili_total", ["bilirubin total", "total bilirubin", "s.bilirubin", "serum bilirubin"]],
  ["inr", ["inr", "international normalised ratio", "international normalized ratio"]],
  ["pt_sec", ["pt (sec)", "prothrombin time", "pt sec", "pt"]],
  ["iron", ["serum iron", "iron"]],
  ["tibc", ["tibc", "total iron binding"]],
  ["ferritin", ["ferritin"]],
  ["neut", ["neutrophil"]],
  ["lymph", ["lymphocyte"]],
  ["mono", ["monocyte"]],
  ["eosino", ["eosinophil"]],
  ["baso", ["basophil"]],
  ["mcv", ["mcv"]],
  ["rbc_count", ["rbc count", "rbc", "red blood cell count"]],
  ["urine_na", ["urine sodium", "urine na"]],
  ["alt", ["alt ", "sgpt"]],
  ["ast", ["ast ", "sgot"]],
  ["platelets", ["platelet"]],
  ["albumin", ["s.albumin", "serum albumin", "albumin"]],
  ["sodium", ["s.sodium", "serum sodium", "sodium"]],
  ["calcium", ["s.calcium", "calcium"]],
  ["urine_alb", ["urine albumin", "microalbumin"]],
  ["urine_creat", ["urine creatinine"]],
  ["acr", ["albumin creatinine ratio", "acr", "uacr"]],
  ["vit_d", ["vitamin d", "25-oh", "25 oh"]],
  ["vit_b12", ["vitamin b12", "b12", "cobalamin"]],
  ["uric_acid", ["uric acid"]],
  ["ggt", ["ggt", "gamma-gt"]],
  ["total_protein", ["total protein"]],
  ["weight", ["weight"]],
  ["bmi", ["bmi"]],
];

export function canonicalKey(testName) {
  const n = (testName || "").toLowerCase().trim();
  if (!n) return null;
  for (const [key, needles] of KEY_MATCH) {
    for (const needle of needles) {
      if (needle.trim() === n) return key;
    }
  }
  for (const [key, needles] of KEY_MATCH) {
    for (const needle of needles) {
      const s = needle.trim();
      if (s.length >= 3 && n.includes(s)) return key;
    }
  }
  return null;
}

function parseNum(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(",", "").trim();
  if (!s) return null;
  const n = parseFloat(s);
  return isFinite(n) ? n : null;
}

function fmtDelta(d) {
  const abs = Math.abs(d);
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  const sign = d > 0 ? "+" : d < 0 ? "−" : "";
  return `${sign}${abs.toFixed(decimals)}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return iso; }
}

export default function TrendChip({ testName, value, latestMap, className = "" }) {
  const key = canonicalKey(testName);
  if (!key || !latestMap || !latestMap[key]) return null;
  const cur = parseNum(value);
  const prev = latestMap[key];
  const prevVal = parseNum(prev.value);
  if (cur === null || prevVal === null) return null;
  const delta = cur - prevVal;

  const better = DIRECTION[key]; // "up" | "down" | null
  let tone = "text-slate-500 bg-slate-100";
  let Icon = Minus;
  if (delta > 0) Icon = TrendingUp;
  else if (delta < 0) Icon = TrendingDown;

  if (delta !== 0 && better) {
    const improved = (better === "down" && delta < 0) || (better === "up" && delta > 0);
    tone = improved ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50";
  } else if (delta === 0) {
    tone = "text-slate-500 bg-slate-100";
  }

  const title = `Previous ${prev.test_name || key}: ${prev.value}${prev.unit ? " " + prev.unit : ""} on ${fmtDate(prev.date)}`;

  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium tabular ${tone} ${className}`}
      title={title}
      data-testid={`trend-chip-${key}`}
    >
      <Icon size={10} />
      <span>{fmtDelta(delta)}</span>
      <span className="opacity-60">vs {prevVal}</span>
    </span>
  );
}
